import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Legend,
  Text,
} from "recharts";

import * as Colors from "../Colors";
import {
  sortData,
  getLongestTick,
  CustomXAxisLabel,
  formatLabelList,
} from "./ChartHelper";

import "./Chart.css";

function getTooltip({ active, label, payload }) {
  if (active) {
    return (
      <div className="Tooltip">
        <p className="label">{`${label}: ${payload[0].value}`}</p>
      </div>
    );
  }
}

function CustomTick(props) {
  const { x, y, payload, textWidth, fontSize } = props;

  const tickValue = isNaN(payload.value)
    ? payload.value
    : Number.parseFloat(payload.value).toFixed(0);

  return (
    <g transform={`translate(${x},${y})`}>
      <Text
        x={0}
        y={0}
        dy={5}
        textAnchor="middle"
        verticalAnchor="start"
        fill={Colors.PURPLE}
        width={textWidth}
        fontSize={fontSize}
      >
        {tickValue}
      </Text>
    </g>
  );
}

function getInitalSelectedBar(data) {
  if (data.length > 0) {
    return Object.entries(data[0])[1][0];
  }
  return null;
}

export default function VerticalBarMulti({ data, options }) {
  const [focusBar, setFocusBar] = useState(null);
  const [selectedBar, setSelectedBar] = useState(getInitalSelectedBar(data));

  data = data ? data : [];
  data = sortData(data, options);

  const textWidth = data.length < 4 ? 150 : data.length < 6 ? 90 : 60;
  const longestTickLength = getLongestTick(data, "name").length;
  const tickFontSize =
    data.length > 3 && longestTickLength > 30 ? "1.2rem" : "1.4rem";
  const yLabel = options.yLabel ?? "Number of Responses";

  function toggleBar(dataKey) {
    setSelectedBar(dataKey);
  }

  function getBarLabelFontSize(data, options) {
    const numBarsPerCategory = Object.entries(data[0]).length;
    if (data.length > 9 && !options.singleSelect) {
      return "1.1rem";
    }
    if (options.singleSelect || numBarsPerCategory < 4) {
      return "1.4rem";
    } else if (numBarsPerCategory < 7) {
      return "1.2rem";
    } else {
      return "1rem";
    }
  }

  function getYDomainMax(data) {
    const maxValue = data.reduce((max, barGroup) => {
      const localMax = Object.keys(barGroup)
        .slice(1) // First element is the data label
        .reduce((barMax, key) => {
          const value = barGroup[key];
          return value > barMax ? value : barMax;
        }, 0);
      return localMax > max ? localMax : max;
    }, 0);

    return Math.ceil((maxValue + 1) / 5) * 5;
  }

  function renderBars(data, options) {
    if (data.length === 0) return;

    const barLabelFontSize = getBarLabelFontSize(data, options);
    const colors =
      options.dataLabel.length > 4 && options.dataLabel.length < 10
        ? Colors.COLOR_MAP_NO_YELLOW[options.dataLabel.length]
        : Colors.COLORS_4;

    return Object.keys(data[0])
      .filter((key) => key.includes("value"))
      .map((key, i) => {
        const opacity = focusBar === null || focusBar === key ? 1 : 0.15;
        return (
          <Bar
            key={key}
            dataKey={key}
            name={options.dataLabel[i]}
            fill={colors[i % colors.length]}
            hide={options.singleSelect && key !== selectedBar}
            style={{ opacity }}
            onMouseEnter={() => {
              setFocusBar(key);
            }}
            onMouseLeave={() => setFocusBar(null)}
            isAnimationActive={false}
          >
            <LabelList
              dataKey={key}
              position="top"
              fill={Colors.PURPLE}
              style={{ fontSize: barLabelFontSize }}
              formatter={(value) => formatLabelList(value, yLabel, options)}
            />
          </Bar>
        );
      });
  }

  function renderLegend(options) {
    if (options.singleSelect) {
      return (
        <Legend
          verticalAlign="top"
          height={50}
          wrapperStyle={{ cursor: "pointer" }}
          iconSize={20}
          iconType={"circle"}
          onClick={({ dataKey }) => toggleBar(dataKey)}
        />
      );
    } else {
      return (
        <Legend
          verticalAlign="top"
          height={50}
          iconSize={20}
          iconType={"square"}
        />
      );
    }
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{
          top: 30,
          right: 15,
          left: -10,
          bottom: 45,
        }}
      >
        {renderLegend(options)}
        {renderBars(data, options)}
        <YAxis
          type="number"
          label={{
            value: yLabel,
            angle: -90,
            fill: Colors.PURPLE,
            dx: 10,
          }}
          tick={false}
          stroke={Colors.PURPLE}
          domain={[0, getYDomainMax(data)]}
        />
        <XAxis
          type="category"
          dataKey="name"
          tickLine={false}
          label={
            <CustomXAxisLabel
              label={options.xLabel}
              startLabel={options.xLabelStart}
              endLabel={options.xLabelEnd}
              dy={longestTickLength > 10 ? 20 : 5}
            />
          }
          tick={<CustomTick textWidth={textWidth} fontSize={tickFontSize} />}
          stroke={Colors.PURPLE}
          width={100}
          interval={0}
        />
        {options.showTooltip ? (
          <Tooltip
            cursor={{ fill: "transparent" }}
            content={(props) => getTooltip(props)}
          />
        ) : null}
      </BarChart>
    </ResponsiveContainer>
  );
}
